import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { useCondominio } from '../contexts/CondominioContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Filter,
  Download,
  Upload,
  Heart,
  X,
  Save
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Pets = () => {
  const { getCondominioId, isAdministrador } = useCondominio();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [showModal, setShowModal] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [apartamentoSelecionado, setApartamentoSelecionado] = useState(null);
  const [moradoresFiltrados, setMoradoresFiltrados] = useState([]);
  const [filters, setFilters] = useState({
    especie: '',
    porte: '',
    ativo: 'true' // Por padrão, mostrar apenas pets ativos
  });

  // Buscar pets
  const { data: petsData, isLoading } = useQuery(
    ['pets', getCondominioId(), searchTerm, filters],
    () => api.get('/pets', {
      params: {
        search: searchTerm,
        ...filters
      }
    }).then(res => res.data.data),
    {
      enabled: !!getCondominioId()
    }
  );

  // Buscar apartamentos para o formulário
  const { data: apartamentosData } = useQuery(
    ['apartamentos', getCondominioId()],
    () => api.get('/apartamentos').then(res => res.data.data.apartamentos),
    {
      enabled: !!getCondominioId()
    }
  );

  // Buscar moradores para o formulário
  const { data: moradoresData } = useQuery(
    ['moradores', getCondominioId()],
    () => api.get('/moradores').then(res => res.data.data.moradores),
    {
      enabled: !!getCondominioId()
    }
  );

  // Inicializar apartamento selecionado para não-administradores quando o modal abre
  React.useEffect(() => {
    if (showModal && !isAdministrador() && user?.vinculos && !editingPet) {
      const vinculoAtivo = user.vinculos.find(v => v.condominio_id === getCondominioId());
      if (vinculoAtivo?.apartamento_id) {
        setApartamentoSelecionado(vinculoAtivo.apartamento_id.toString());
      }
    }
  }, [showModal, isAdministrador, user, editingPet]);

  // Filtrar moradores quando o apartamento mudar
  React.useEffect(() => {
    if (apartamentoSelecionado && moradoresData) {
      const moradoresFilt = moradoresData.filter(
        m => m.apartamento_id === parseInt(apartamentoSelecionado)
      );
      setMoradoresFiltrados(moradoresFilt);
      console.log('🏢 Moradores filtrados do apartamento', apartamentoSelecionado, ':', moradoresFilt.length);
    } else if (!apartamentoSelecionado) {
      setMoradoresFiltrados([]);
    }
  }, [apartamentoSelecionado, moradoresData]);

  // Criar/atualizar pet
  const petMutation = useMutation(
    (petData) => {
      if (editingPet) {
        return api.put(`/pets/${editingPet.pet_id}`, petData);
      } else {
        return api.post('/pets', petData);
      }
    },
    {
      onSuccess: (response) => {
        // Invalidar e refetch
        queryClient.invalidateQueries(['pets']);
        queryClient.refetchQueries(['pets']);
        
        // Mostrar notificação de sucesso
        if (window.showNotification) {
          const message = editingPet ? 'Pet atualizado com sucesso!' : 'Pet cadastrado com sucesso!';
          window.showNotification(message, 'success');
        }
        
        // Fechar modal e limpar formulário
        setShowModal(false);
        setEditingPet(null);
        setSelectedImage(null);
        setImagePreview(null);
      },
      onError: (error) => {
        console.error('Erro ao salvar pet:', error);
        
        // A mensagem de erro será exibida pelo componente de notificação
        if (window.showNotification) {
          const message = error.response?.data?.message || 'Erro ao salvar pet';
          window.showNotification(message, 'error');
        }
      }
    }
  );

  // Excluir pet
  const deleteMutation = useMutation(
    (id) => api.delete(`/pets/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pets']);
      }
    }
  );

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Adicionar condominio_id ao FormData
    formData.append('condominio_id', getCondominioId());

    // Adicionar imagem se selecionada
    if (selectedImage) {
      formData.append('foto', selectedImage);
    }
    
    // Converter campos booleanos
    formData.set('pet_castrado', formData.get('pet_castrado') === 'on');
    formData.set('pet_vacinado', formData.get('pet_vacinado') === 'on');
    formData.set('pet_ativo', !!formData.get('pet_ativo'));
    
    // Tratar campo morador_id - se vazio, não enviar
    const moradorId = formData.get('morador_id');
    if (!moradorId || moradorId === '') {
      formData.delete('morador_id');
    }
    
    petMutation.mutate(formData);
  };

  const handleEdit = (pet) => {
    setEditingPet(pet);
    // Inicializar apartamento selecionado para filtrar moradores
    if (pet.apartamento_id) {
      setApartamentoSelecionado(pet.apartamento_id.toString());
    }
    // Se há foto, adicionar URL base para preview
    if (pet.pet_foto) {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      setImagePreview(`${baseUrl}${pet.pet_foto}`);
    } else {
      setImagePreview(null);
    }
    setSelectedImage(null);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este pet?')) {
      deleteMutation.mutate(id);
    }
  };

  const resetForm = () => {
    setEditingPet(null);
    setSelectedImage(null);
    setImagePreview(null);
    setApartamentoSelecionado(null);
    setMoradoresFiltrados([]);
    setShowModal(false);
  };

  const getEspecieIcon = (especie) => {
    const icons = {
      cachorro: '🐕',
      gato: '🐱',
      passaro: '🐦',
      peixe: '🐠',
      outros: '🐾'
    };
    return icons[especie] || '🐾';
  };

  const getPorteColor = (porte) => {
    const colors = {
      pequeno: 'text-green-600',
      medio: 'text-yellow-600',
      grande: 'text-red-600'
    };
    return colors[porte] || 'text-gray-600';
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Heart className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Pets</h1>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Cadastrar Pet
        </button>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Espécie</label>
            <select
              value={filters.especie}
              onChange={(e) => setFilters({...filters, especie: e.target.value})}
              className="input"
            >
              <option value="">Todas</option>
              <option value="cachorro">Cachorro</option>
              <option value="gato">Gato</option>
              <option value="passaro">Pássaro</option>
              <option value="peixe">Peixe</option>
              <option value="outros">Outros</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Porte</label>
            <select
              value={filters.porte}
              onChange={(e) => setFilters({...filters, porte: e.target.value})}
              className="input"
            >
              <option value="">Todos</option>
              <option value="pequeno">Pequeno</option>
              <option value="medio">Médio</option>
              <option value="grande">Grande</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.ativo}
              onChange={(e) => setFilters({...filters, ativo: e.target.value})}
              className="input"
            >
              <option value="">Todos</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <input
              type="text"
              placeholder="Nome ou raça..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => queryClient.invalidateQueries(['pets'])}
              className="btn btn-primary"
            >
              <Search className="h-4 w-4 mr-2" />
              Filtrar
            </button>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilters({
                  especie: '',
                  porte: '',
                  ativo: 'true'
                });
              }}
              className="btn btn-secondary"
            >
              Limpar
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* Lista de Pets */}
      <div className="card">
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Foto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Espécie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Porte
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Morador
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Apartamento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {petsData?.pets?.map((pet) => (
                  <tr key={pet.pet_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-2xl mr-3">
                          {getEspecieIcon(pet.pet_especie)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {pet.pet_nome}
                          </div>
                          <div className="text-sm text-gray-500">
                            {pet.pet_raca || 'Raça não informada'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {pet.pet_foto ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${pet.pet_foto}`}
                          alt={pet.pet_nome}
                          className="w-12 h-12 object-cover rounded-lg border border-gray-300"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-2xl">
                            {getEspecieIcon(pet.pet_especie)}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="capitalize text-sm text-gray-900">
                        {pet.pet_especie}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`capitalize text-sm ${getPorteColor(pet.pet_porte)}`}>
                        {pet.pet_porte || 'Não informado'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {pet.morador?.morador_nome || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {pet.apartamento ? 
                        `${pet.apartamento.apartamento_bloco} - ${pet.apartamento.apartamento_numero}` : 
                        'N/A'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        pet.pet_ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {pet.pet_ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(pet)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(pet.pet_id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container custom-scrollbar">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingPet ? 'Editar Pet' : 'Cadastrar Pet'}
              </h3>
              <button
                onClick={resetForm}
                className="modal-close-btn"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
              
              <form onSubmit={handleSubmit}>
                <div className="form-grid form-grid-2">
                  <div className="form-field">
                    <label className="form-label form-label-required">
                      Nome do Pet
                    </label>
                    <input
                      type="text"
                      name="pet_nome"
                      defaultValue={editingPet?.pet_nome || ''}
                      required
                      className="form-input"
                      placeholder="Digite o nome do pet"
                    />
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label form-label-required">
                      Espécie
                    </label>
                    <select
                      name="pet_especie"
                      defaultValue={editingPet?.pet_especie || ''}
                      required
                      className="form-select"
                    >
                      <option value="">Selecione</option>
                      <option value="cachorro">Cachorro</option>
                      <option value="gato">Gato</option>
                      <option value="passaro">Pássaro</option>
                      <option value="peixe">Peixe</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">
                      Raça
                    </label>
                    <input
                      type="text"
                      name="pet_raca"
                      defaultValue={editingPet?.pet_raca || ''}
                      className="form-input"
                      placeholder="Digite a raça"
                    />
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">
                      Cor
                    </label>
                    <input
                      type="text"
                      name="pet_cor"
                      defaultValue={editingPet?.pet_cor || ''}
                      className="form-input"
                      placeholder="Digite a cor"
                    />
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">
                      Porte
                    </label>
                    <select
                      name="pet_porte"
                      defaultValue={editingPet?.pet_porte || 'nao_informado'}
                      className="form-select"
                    >
                      <option value="nao_informado">Não informado</option>
                      <option value="pequeno">Pequeno</option>
                      <option value="medio">Médio</option>
                      <option value="grande">Grande</option>
                    </select>
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">
                      Sexo
                    </label>
                    <select
                      name="pet_sexo"
                      defaultValue={editingPet?.pet_sexo || 'nao_informado'}
                      className="form-select"
                    >
                      <option value="nao_informado">Não informado</option>
                      <option value="macho">Macho</option>
                      <option value="femea">Fêmea</option>
                    </select>
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">
                      Data de Nascimento
                    </label>
                    <input
                      type="date"
                      name="pet_data_nascimento"
                      defaultValue={editingPet?.pet_data_nascimento || new Date().toISOString().split('T')[0]}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">
                      Foto do Pet
                    </label>
                    <div className="file-input-container">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        id="pet-foto"
                      />
                      <label htmlFor="pet-foto" className="file-input-label">
                        <Upload className="h-4 w-4" style={{marginRight: '0.5rem'}} />
                        {selectedImage ? 'Arquivo selecionado' : 'Clique para selecionar uma foto'}
                      </label>
                    </div>
                    {imagePreview && (
                      <div className="file-preview">
                        <img
                          src={imagePreview}
                          alt="Preview"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label form-label-required">
                      Apartamento
                    </label>
                    {!isAdministrador() ? (
                      // Morador só pode cadastrar pet no seu apartamento
                      <>
                        <input
                          type="hidden"
                          name="apartamento_id"
                          value={user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id || ''}
                        />
                        <input
                          type="text"
                          value={apartamentosData?.find(apt => 
                            apt.apartamento_id === user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id
                          )?.apartamento_numero ? 
                            `${apartamentosData?.find(apt => 
                              apt.apartamento_id === user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id
                            )?.apartamento_bloco} - ${apartamentosData?.find(apt => 
                              apt.apartamento_id === user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id
                            )?.apartamento_numero}` : 'Nenhum apartamento vinculado'
                          }
                          disabled
                          className="form-input"
                          title="Como morador, você só pode cadastrar pets no seu apartamento"
                        />
                        {!user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id && (
                          <p className="form-error-message">
                            Você precisa estar vinculado a um apartamento para cadastrar pets
                          </p>
                        )}
                      </>
                    ) : (
                      // Administradores podem escolher qualquer apartamento
                      <select
                        name="apartamento_id"
                        defaultValue={editingPet?.apartamento_id || ''}
                        required
                        className="form-select"
                        onChange={(e) => {
                          setApartamentoSelecionado(e.target.value);
                          // Limpar morador selecionado quando trocar apartamento
                          const moradorSelect = document.querySelector('select[name="morador_id"]');
                          if (moradorSelect) moradorSelect.value = '';
                        }}
                      >
                        <option value="">Selecione</option>
                        {apartamentosData?.map((apartamento) => (
                          <option key={apartamento.apartamento_id} value={apartamento.apartamento_id}>
                            {apartamento.apartamento_bloco} - {apartamento.apartamento_numero}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">
                      Morador Responsável
                    </label>
                    <select
                      name="morador_id"
                      defaultValue={editingPet?.morador_id || ''}
                      className="form-select"
                      disabled={isAdministrador() && !apartamentoSelecionado}
                    >
                      <option value="">
                        {isAdministrador() && !apartamentoSelecionado 
                          ? 'Primeiro selecione um apartamento' 
                          : 'Selecione (opcional)'}
                      </option>
                      {(isAdministrador() ? moradoresFiltrados : moradoresData)?.map((morador) => (
                        <option key={morador.morador_id} value={morador.morador_id}>
                          {morador.morador_nome}
                        </option>
                      ))}
                    </select>
                    {isAdministrador() && apartamentoSelecionado && moradoresFiltrados.length === 0 && (
                      <p className="form-error-message" style={{color: '#6b7280'}}>
                        Nenhum morador cadastrado neste apartamento
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="form-field form-field-full">
                  <label className="form-label">
                    Observações
                  </label>
                  <textarea
                    name="pet_observacoes"
                    defaultValue={editingPet?.pet_observacoes || ''}
                    rows={3}
                    className="form-textarea"
                    placeholder="Digite observações sobre o pet..."
                  />
                </div>
                
                <div className="form-field form-field-full" style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
                  <label className="form-label" style={{marginBottom: 0}}>
                    <input
                      type="checkbox"
                      name="pet_castrado"
                      defaultChecked={editingPet?.pet_castrado || false}
                      className="form-checkbox"
                    />
                    {' '}Castrado
                  </label>
                  
                  <label className="form-label" style={{marginBottom: 0}}>
                    <input
                      type="checkbox"
                      name="pet_vacinado"
                      defaultChecked={editingPet?.pet_vacinado || false}
                      className="form-checkbox"
                    />
                    {' '}Vacinado
                  </label>
                  
                  <label className="form-label" style={{marginBottom: 0}}>
                    <input
                      type="checkbox"
                      name="pet_ativo"
                      defaultChecked={editingPet?.pet_ativo !== false}
                      className="form-checkbox"
                    />
                    {' '}Ativo
                  </label>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-base btn-secondary-custom"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={petMutation.isLoading}
                    className="btn-base btn-primary-custom btn-icon"
                  >
                    <Save className="h-4 w-4" />
                    {petMutation.isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pets;
